import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { locationKeyFromName } from '../lib/schedule.js';
import { AvailabilityLegend } from './AvailabilityLegend.jsx';
import { QuietCalendar } from './QuietCalendar.jsx';
import { AttentionList } from './AttentionList.jsx';
import { DayDetailPopover } from './DayDetailPopover.jsx';
import { FeasibilityPanel } from './FeasibilityPanel.jsx';
import { AvailabilityHeatmap } from './AvailabilityHeatmap.jsx';
import { TimeOffReportView } from './TimeOffReportView.jsx';

export function AvailabilityHealthView({ user, location }) {
  const headers = { 'x-user-id': String(user.id) };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshAt, setRefreshAt] = useState(null);
  // CJ 2026-07-29 removed the chain-wide All·14 / All·56 views as not useful;
  // this is per-location only. 2026-08-01: `mode` and `tab` were left behind as
  // state that could only ever hold 'single' and 'outlook' — setTab was never
  // called and setMode only ever with 'single' — so every `mode === 'all'` and
  // `tab === 'map'` branch was unreachable, including a live fetch to
  // /api/health/availability/all that could not fire. Removed along with the
  // ignored `mode` prop the mount site was still passing.
  const locKey = location ? locationKeyFromName(location.name) : null;

  const [selectedDay, setSelectedDay] = useState(null);
  async function load() {
    setLoading(true); setError('');
    try {
      const r = await api('GET', `/api/health/operations-command/${locKey}?days=56`, null, headers);
      setData(r); setRefreshAt(new Date());
    } catch (e) {
      let msg = e.message || 'Failed to load';
      try { const p = JSON.parse(msg); msg = p.error || msg; } catch {}
      setError(msg);
    }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [locKey]);
  useEffect(() => { setSelectedDay(null); }, [locKey]);

  const subtitle = location?.name || '—';

  const tierColors = {
    green:  { bg: '#e8f5e9', stripe: '#2e7d32', label: '🟢 Healthy' },
    yellow: { bg: '#fff8e1', stripe: '#f9a825', label: '🟡 Monitor' },
    orange: { bg: '#fff3e0', stripe: '#ef6c00', label: '🟠 At Risk' },
    red:    { bg: '#ffebee', stripe: '#c62828', label: '🔴 Critical' },
    gray:   { bg: '#f5f5f5', stripe: '#9e9e9e', label: '⚪ Closed' },
  };

  return (
    <div className="space-y-3">
      {error && <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
        <div className="text-xs text-red-700">{error}</div>
        <button onClick={load} className="btn-secondary mt-2" style={{ fontSize: 12 }}>Retry</button>
      </div>}
      {loading && !data && <div className="card text-sm text-gray-500">Loading 14-day health…</div>}
      {data && (
        <>
          {/* Header — title + summary pills */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#212121' }}>
                  Availability health · {subtitle}
                </div>
                <div style={{ fontSize: 11, color: '#757575' }}>
                  14-day outlook
                  {refreshAt && ` · refreshed ${Math.max(1, Math.floor((Date.now() - refreshAt.getTime()) / 60000))} min ago`}
                  {' · '}{data.activeEmployees} active employees
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {['red', 'orange', 'yellow', 'green'].map((t) => (
                  <div key={t} style={{ padding: '4px 10px', borderRadius: 999,
                                          background: tierColors[t].bg, fontSize: 11, fontWeight: 700,
                                          border: `1px solid ${tierColors[t].stripe}` }}>
                    <span style={{ color: tierColors[t].stripe }}>{tierColors[t].label.slice(2)}: {data.tierCounts[t] || 0}</span>
                  </div>
                ))}
                <button onClick={load} className="btn-secondary" style={{ fontSize: 11 }}>↻</button>
              </div>
            </div>
          </div>

          {/* CJ 2026-07-06 (v2): single-location view is now a quiet
              8-week calendar + attention list. Healthy days visually
              disappear so problems pop. Tap any calendar cell OR any
              attention row for the full day detail popover. */}
          <>
              {/* CJ 2026-07-29 (Level B): the headline answer — can we legally
                  staff the template this week, and what's unfillable? Replaces
                  the old 177/300 forecast chart. Per café, week-selectable. */}
              <FeasibilityPanel user={user} location={location} />
              {/* CJ 2026-08-02: time off belongs next to the staffing
                  signals, not behind the approvals queue — "can I staff
                  this week" and "who is away" are the same question.
                  Collapsed by default so it doesn't push Feasibility down. */}
              <TimeOffReportView currentUser={user} location={location} />
              {/* CJ 2026-07-29: staffing signals the tier colors can't show —
                  the regular pool vs backup reserve, who hasn't confirmed
                  availability, and who's leaving inside the window. */}
              <div className="card">
                <div style={{ fontSize: 12, color: '#37474f', display: 'flex', flexWrap: 'wrap', gap: '4px 14px', alignItems: 'baseline' }}>
                  <span><b>{data.activeEmployees}</b> regulars here{data.backupRoster ? ` · +${data.backupRoster} backup from other cafés` : ''}</span>
                  {data.demandSource && <span style={{ color: '#757575' }}>vs {data.demandSource === 'template' ? 'current template' : data.demandSource === 'schedule' ? 'recent schedule' : 'no target set'}</span>}
                </div>
                {/* CJ 2026-08-06: this was a warning — "N people have no
                    availability on file, a green day may be optimistic" — which
                    contradicted the headcount immediately above it, since the
                    supply maths already counts these people as fully available.
                    availability_reductions stores CONSTRAINTS; no rows means no
                    constraints. So they are listed as what they are. Only a
                    recent hire gets a caveat, because that is the one case where
                    "open" is an assumption rather than a fact. */}
                {data.openAvailability > 0 && (
                  <div style={{ marginTop: 8, padding: '8px 10px', background: '#f1f7f2',
                                borderLeft: '4px solid var(--forest)', borderRadius: 4 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#2e5d42' }}>
                      Open availability — {data.openAvailability} {data.openAvailability === 1 ? 'person' : 'people'}, no constraints on file
                    </div>
                    {data.openAvailabilityList?.map(p => (
                      <div key={p.user_id} style={{ fontSize: 12, color: '#37474f', marginTop: 3 }}>
                        <b>{p.name}</b>
                        {p.days_since_hire != null && p.days_since_hire <= 30 && (
                          <span style={{ color: '#8a5a1f' }}>
                            {' · '}started {p.days_since_hire === 0 ? 'today' : p.days_since_hire === 1 ? 'yesterday' : `${p.days_since_hire} days ago`} — may not have been asked yet
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* Request #14 (CJ 2026-08-02): a departure used to be one grey-ish
                    line that disappeared the day it took effect — so the moment it
                    actually mattered, the numbers dropped with nothing on screen to
                    explain why. Already-gone comes first and loudest, because it is
                    the one that is silently moving every figure above it. */}
                {data.departed?.length > 0 && (
                  <div style={{ marginTop: 8, padding: '8px 10px', background: '#ffebee',
                                borderLeft: '4px solid #c62828', borderRadius: 4 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#c62828' }}>
                      ⛔ No longer on the roster — already removed from every number above
                    </div>
                    {data.departed.map(d => (
                      <div key={d.user_id} style={{ fontSize: 12, color: '#5f2120', marginTop: 3 }}>
                        <b>{d.name}</b> · last day {d.last_day}
                        {d.days_since === 1 ? ' (yesterday)' : ` (${d.days_since} days ago)`}
                        {d.last_day_reason ? ` — ${d.last_day_reason}` : ''}
                      </div>
                    ))}
                    <div style={{ fontSize: 11, color: '#8a5a1f', marginTop: 4 }}>
                      Their headcount and availability are gone. If the outlook got worse, this is why — backfill or cut the template.
                    </div>
                  </div>
                )}
                {data.departures?.length > 0 && (
                  <div style={{ marginTop: 8, padding: '8px 10px', background: '#fff3e0',
                                borderLeft: '4px solid #ef6c00', borderRadius: 4 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#ef6c00' }}>
                      ⚠ Leaving within the window — {data.departures.length} {data.departures.length === 1 ? 'person' : 'people'}
                    </div>
                    {data.departures.map(d => (
                      <div key={d.user_id} style={{ fontSize: 12, color: '#5d4037', marginTop: 3 }}>
                        <b>{d.name}</b> · last day {d.last_day}
                        {d.days_until === 0 ? ' (today)' : d.days_until === 1 ? ' (tomorrow)' : ` (in ${d.days_until} days)`}
                        {d.last_day_reason ? ` — ${d.last_day_reason}` : ''}
                      </div>
                    ))}
                  </div>
                )}
                {data.arrivals?.length > 0 && (
                  <div style={{ fontSize: 12, color: '#2e7d32', marginTop: 6 }}>
                    Starting soon: {data.arrivals.map(d => `${d.name} (${d.hire_date})`).join(' · ')}
                  </div>
                )}
              </div>
              <AvailabilityHeatmap user={user} location={location} />
              <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>8-week outlook</div>
                  <AvailabilityLegend tierColors={tierColors} />
                </div>
                <QuietCalendar days={data.days} onSelect={setSelectedDay} tierColors={tierColors} selectedYmd={selectedDay?.ymd} />
              </div>
              <div className="card">
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
                  Attention required
                </div>
                <AttentionList days={data.days} onSelect={setSelectedDay} tierColors={tierColors} />
              </div>
              {selectedDay && (
                <DayDetailPopover day={selectedDay} tierColors={tierColors} onClose={() => setSelectedDay(null)} />
              )}
          </>
        </>
      )}

      <div className="text-xs text-gray-400 px-1">
        Set operating hours and minimum staffing in Settings → Locations to populate this view.
      </div>
    </div>
  );
}
